// MoneyCell — renders an EXACT decimal money string in Geist Mono, tabular-nums.
//
// MONEY INVARIANT: amounts are exact decimal STRINGS from the backend and are
// rendered VERBATIM. We never parseFloat / Number() them (float rounding would
// corrupt a ledger figure). Tone colors a credit (jade), debit (red), or profit
// (copper) figure; the default tone is the plain ledger surface text.
//
// `signed` only affects the +/- prefix for credit/debit framing; it never alters
// the underlying string's digits.

import { cn } from "@/lib/utils";

export type MoneyTone = "default" | "credit" | "debit" | "profit" | "muted";

const toneClass: Record<MoneyTone, string> = {
  default: "text-nx-text",
  credit: "text-nx-credit",
  debit: "text-nx-debit",
  profit: "text-nx-profit",
  muted: "text-nx-text-3",
};

export function MoneyCell({
  value,
  currency = "USD",
  tone = "default",
  signed,
  className,
}: {
  // Exact decimal string, e.g. "1250.50". Never a number.
  value: string;
  currency?: string;
  tone?: MoneyTone;
  // "+" for credit, "-" for debit — presentation only.
  signed?: "+" | "-";
  className?: string;
}) {
  const symbol = currency === "USD" ? "$" : "";
  return (
    <span
      data-slot="money"
      className={cn(
        "font-mono text-[0.8125rem] tabular-nums whitespace-nowrap",
        toneClass[tone],
        className,
      )}
      title={`${currency} ${value}`}
    >
      {signed}
      {symbol}
      {value}
      {!symbol && <span className="ml-1 text-nx-text-3">{currency}</span>}
    </span>
  );
}
